// _services/packagesService.js
import api from '../../../api/axios';

export const packagesService = {
  /**
   * Obtener paquetes del template
   */
  getPackages: async (slug, token = null) => {
    try {
      let response;
      if (token) {
        response = await api.get(`/admin/template/${slug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await api.get(`/forms/template/${slug}`);
      }
      
      if (response.data && response.data.packages) {
        return response.data.packages.map(pkg => ({
          // 🟢 FIX: usar ?? en vez de || para no descartar un id_package=0 válido.
          // ⚠️ Si el backend devuelve un paquete "por defecto" sin id real (null),
          // ese paquete quedará con id_package: null y no podrá guardarse ni
          // registrarse correctamente hasta que el backend le asigne un id.
          id_package: pkg.id_package ?? pkg.id ?? null,
          title: pkg.title || '',
          description: pkg.description || '',
          amount: pkg.amount || 0,
          currency: pkg.currency || 'Bs.',
          number_of_competitors: pkg.number_of_competitors || 1,
          conversion_rate: pkg.conversion_rate || 6.96,
          includes: pkg.includes || [],
          addons: pkg.addons || [],
          startDate: pkg.startDate ? pkg.startDate.split('T')[0] : '',
          endDate: pkg.endDate ? pkg.endDate.split('T')[0] : '',
          is_closed: pkg.is_closed || false,
          image: pkg.image || null,
        }));
      }
      return [];
    } catch (error) {
      console.error('Error al cargar paquetes:', error);
      throw error;
    }
  },

  /**
   * Guardar paquetes con imágenes
   */
  savePackages: async (slug, packageForms, token) => {
    const formData = new FormData();
    
    // Preparar datos de paquetes
    const packagesToSend = packageForms.map((pkg, index) => ({
      id_package: pkg.id_package || null,
      title: pkg.title || '',
      description: pkg.description || '',
      amount: parseFloat(pkg.amount) || 0,
      currency: pkg.currency || 'Bs.',
      number_of_competitors: parseInt(pkg.number_of_competitors) || 1,
      conversion_rate: parseFloat(pkg.conversion_rate) || 6.96,
      includes: pkg.includes.map(include => ({
        id: include.id || `inc-${Date.now()}-${Math.random()}`,
        label: include.label || '',
        required: include.required || false,
      })),
      addons: (pkg.addons || []).map(addon => ({
        id: addon.id || `addon-${Date.now()}-${Math.random()}`,
        label: addon.label || '',
        extra_amount: parseFloat(addon.extra_amount) || 0,
      })),
      startDate: pkg.startDate || '',
      endDate: pkg.endDate || '',
      is_closed: pkg.is_closed || false,
      image: pkg.imageFile ? null : (pkg.image || null),
    }));

    // Agregar paquetes como JSON
    formData.append('packages', JSON.stringify(packagesToSend));
    
    // Agregar imágenes si existen
    packageForms.forEach((pkg, index) => {
      if (pkg.imageFile instanceof File) {
        formData.append(`packages[${index}][image]`, pkg.imageFile);
      }
    });

    const response = await api.put(
      `/admin/template/${slug}/packages`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  /**
   * Validar un paquete
   */
  validatePackage: (pkg) => {
    if (!pkg.title?.trim()) return 'El título es obligatorio.';
    if (!pkg.amount || pkg.amount <= 0) return 'El monto debe ser mayor a 0.';
    if (!pkg.number_of_competitors || pkg.number_of_competitors < 1) {
      return 'El número de competidores debe ser al menos 1.';
    }
    if (!pkg.conversion_rate || pkg.conversion_rate <= 0) {
      return 'La tasa de conversión debe ser mayor a 0.';
    }
    if (!pkg.startDate) return 'La fecha de inicio es obligatoria.';
    if (!pkg.endDate) return 'La fecha de fin es obligatoria.';
    if (new Date(pkg.startDate) > new Date(pkg.endDate)) {
      return 'La fecha de inicio no puede ser posterior a la fecha de fin.';
    }
    return null;
  },

  /**
   * Formatear fecha
   */
  formatDate: (dateString) => {
    if (!dateString) return 'No definida';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  },

  /**
   * Obtener etiqueta de competidor
   */
  getCompetitorLabel: (numberOfCompetitors) => {
    switch(parseInt(numberOfCompetitors)) {
      case 1: return 'Individual';
      case 2: return 'Duplas';
      case 3: return 'Tríos';
      case 4: return 'Equipos de 4';
      case 5: return 'Equipos de 5';
      case 6: return 'Equipos de 6';
      default: return `Equipos de ${numberOfCompetitors}`;
    }
  },

  /**
   * Calcular monto total con addons
   */
  calculateTotalAmount: (pkg, selectedAddons) => {
    let amountPerPerson = parseFloat(pkg.amount) || 0;
    const addonsElegidos = selectedAddons[pkg.id_package] || {};
    const extraPorAddons = (pkg.addons || [])
      .filter(addon => addonsElegidos[addon.id])
      .reduce((sum, addon) => sum + (parseFloat(addon.extra_amount) || 0), 0);
    amountPerPerson += extraPorAddons;
    const numberOfCompetitors = parseInt(pkg.number_of_competitors) || 1;
    return (amountPerPerson * numberOfCompetitors).toFixed(2);
  },

  /**
   * Convertir a USD
   */
  convertToUSD: (pkg, totalBs) => {
    const conversionRate = parseFloat(pkg.conversion_rate) || 6.96;
    return (totalBs / conversionRate).toFixed(2);
  },

  /**
   * Crear objeto de paquete para selección
   */
  createPackageForSelection: (pkg, selectedAddons) => {
    const addonsElegidos = selectedAddons[pkg.id_package] || {};
    const extraTotal = (pkg.addons || [])
      .filter(a => addonsElegidos[a.id])
      .reduce((sum, a) => sum + (parseFloat(a.extra_amount) || 0), 0);

    return {
      id: pkg.id_package || pkg.id,
      id_package: pkg.id_package || pkg.id,
      title: pkg.title || '',
      description: pkg.description || '',
      amount: parseFloat(pkg.amount) + extraTotal,
      currency: pkg.currency || 'Bs.',
      number_of_competitors: parseInt(pkg.number_of_competitors) || 1,
      conversion_rate: parseFloat(pkg.conversion_rate) || 6.96,
      includes: pkg.includes || [],
      addons: pkg.addons || [],
      addonsElegidos: addonsElegidos,
      startDate: pkg.startDate || '',
      endDate: pkg.endDate || '',
      is_closed: pkg.is_closed || false,
      image: pkg.image || null,
    };
  },

  /**
   * Crear paquete vacío para nuevo formulario
   */
  createEmptyPackage: () => ({
    id_package: null,
    title: '',
    description: '',
    amount: 0,
    currency: 'Bs.',
    number_of_competitors: 1,
    conversion_rate: 6.96,
    includes: [],
    addons: [],
    startDate: '',
    endDate: '',
    is_closed: false,
    image: null,
    imageFile: null,
    imagePreview: null,
  }),
};